/**
 * A `thinking` block, in both of its lives.
 *
 * One block, one flag. While `live` it is the indicator with the reasoning rolling under it; once
 * `sealThinking` flips the flag it is the collapsible "Thought for N sec" recap. The text does not
 * move between two components as it settles — that is the whole reason `TurnBuilder` carries `live`
 * on the block instead of holding two.
 *
 * ## The recap keeps one visual size when it opens
 *
 * Folded it uses the collapsed disclosure geometry and open uses the open geometry, but both use the
 * same 14/20 label typography. The disclosure affordance can change; the text must not jump when the
 * user clicks it.
 *
 * The step between the two tiers is now only the affordance, not the text size. `ActivityRow` derives
 * `aria-expanded` from `density` alone ("`open` draws its body; `collapsed`
 * has folded it away. The chevron says the same thing in pixels"), so a row that kept the collapsed
 * tier while showing a body would announce itself as folded while standing open. The same flip is
 * what `ActivityCard` does to a tool card the reader folds, so the two behave alike.
 *
 * ## The recap's own mark, and its own voice
 *
 * `tool="thinking"` — the brain — and NOT `agent`. These rows asked for `agent` until 2026-09-20,
 * which meant the dot matrix, which `ActivityGlyph` draws only while it is moving: a settled recap
 * is the one row guaranteed never to be loading, so it had no mark at all, and before that it wore
 * a frozen one that Beka read as broken. The brain says what the row IS rather than that something
 * is happening, which is what a row that only exists after it has stopped needs.
 *
 * The REASONING is the one italic run in the thread; the label is not. See
 * `ThinkingBlock.module.css` — `className` reaches the block, and the rule there picks out the body
 * from the header rather than slanting both.
 */
import { useCallback, useState } from 'react';
import { ActivityBlock, ActivityGlyph, type ChatTheme } from '~/design-system/components/v1_5/Chat';
import type { TurnBlock } from '../turnModel';
import { ThinkingIndicator } from './ThinkingIndicator';
import styles from './ThinkingBlock.module.css';

export type ThinkingTurnBlock = Extract<TurnBlock, { kind: 'thinking' }>;

export interface ThinkingBlockProps {
  block: ThinkingTurnBlock;
  /**
   * The turn's clock, for the live tier. Required in practice while `block.live` is true — the
   * indicator is not an indicator without it — and ignored once the block has settled.
   */
  elapsedSec?: number;
  theme: ChatTheme;
  /** Start the settled recap open. Off by default: a finished thought is a footnote. */
  defaultExpanded?: boolean;
}

/**
 * `Thought for 9 sec`.
 *
 * `sealThinking` always records a duration, so the fallback is only reachable on a
 * `THINKING_CHECKPOINT` rehydrated from a backend version that did not send one. It says what the
 * row is rather than inventing a number.
 */
function recapLabel(durationSec: number | undefined): string {
  return durationSec ? `Thought for ${durationSec} sec` : 'Reasoning';
}

export function ThinkingBlock({ block, elapsedSec = 0, theme, defaultExpanded = false }: ThinkingBlockProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const toggle = useCallback(() => setExpanded((was) => !was), []);

  if (block.live) {
    return <ThinkingIndicator elapsedSec={elapsedSec} reasoning={block.text} theme={theme} />;
  }

  const text = block.text.trim();
  const label = recapLabel(block.durationSec);

  // Nothing to unfold — a checkpoint that carried a duration and no words. The row stays as the
  // record that the model thought, without a chevron promising something behind it.
  if (!text) {
    return (
      <ActivityBlock
        theme={theme}
        className={styles.thought}
        row={{
          density: 'collapsed',
          icon: <ActivityGlyph tool="thinking" size={15} tone="hint" theme={theme} />,
          label,
          visual: 'stable',
          theme,
        }}
      />
    );
  }

  return (
    <ActivityBlock
      theme={theme}
      className={styles.thought}
      row={
        expanded
          ? {
              density: 'open',
              icon: <ActivityGlyph tool="thinking" size={16} theme={theme} />,
              label,
              visual: 'stable',
              disclosure: 'up',
              onToggle: toggle,
              theme,
            }
          : {
              density: 'collapsed',
              // 15 **and** hint — both halves of the collapsed tier, the trap `DoneStep` names.
              icon: <ActivityGlyph tool="thinking" size={16} tone="hint" theme={theme} />,
              label,
              visual: 'stable',
              disclosure: 'down',
              onToggle: toggle,
              theme,
            }
      }
      /* `explanation`, not `list`: reasoning is a paragraph, and the body that draws a paragraph is
         the one Failed uses for its reason — 14/22 `subtle`, full width, no rim. */
      body={expanded ? { kind: 'explanation', text } : { kind: 'none' }}
    />
  );
}
